import React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { LegalPageShell } from "@/components/legal/LegalPageShell";

/**
 * Mind Media — Terms of Service
 * Route: /terms
 *
 * Drop-in React component. Plain React + Tailwind core utilities only,
 * no external dependencies, so it works in any Vite/Next/Lovable build.
 *
 * BEFORE PUBLISHING — search this file for "REVIEW:" comments. Each one marks
 * a place where the language has to match what you actually do. Every promise
 * below was written to mirror a specific claim on mindmedia.co. If you change
 * the marketing copy, change this page the same day.
 */

const LAST_UPDATED = "August 4, 2026";
const CONTACT_EMAIL = "hello@mindmedia.co";
const MAILING_ADDRESS =
  "1741 Eastlake Pkwy, Ste 102 #2009, Chula Vista, CA 91915";

type SectionProps = {
  id: string;
  title: string;
  children: React.ReactNode;
};

function Section({ id, title, children }: SectionProps) {
  return (
    <section id={id} className="scroll-mt-24">
      <h2 className="mt-12 mb-4 text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
        {title}
      </h2>
      <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
        {children}
      </div>
    </section>
  );
}

function Bullets({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="ml-5 list-disc space-y-2 text-muted-foreground marker:text-primary/60">
      {items.map((item, i) => (
        <li key={i} className="pl-1">
          {item}
        </li>
      ))}
    </ul>
  );
}

function Callout({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="my-6 rounded-lg border border-border bg-muted/40 p-5">
      <p className="text-sm font-semibold uppercase tracking-wide text-foreground">
        {title}
      </p>
      <div className="mt-2 space-y-3 text-sm leading-relaxed text-muted-foreground">
        {children}
      </div>
    </div>
  );
}

function TermsPage() {
  return (
    <LegalPageShell title="Terms of Service" lastUpdated={LAST_UPDATED}>


        <div className="mt-8 space-y-4 text-base leading-relaxed text-muted-foreground">
          <p>
            These Terms of Service (the &ldquo;Terms&rdquo;) are a contract
            between you and Mind Media LLC, a Delaware limited liability company
            (&ldquo;Mind Media,&rdquo; &ldquo;we,&rdquo; &ldquo;us&rdquo;). They
            apply when you use{" "}
            <span className="whitespace-nowrap">mindmedia.co</span> and when you
            hire us to design, build, host, or support a website.
          </p>
          <p>
            By using the site, requesting a mockup, booking a call, or paying an
            invoice, you agree to these Terms. If you are agreeing on behalf of
            a company, you confirm you have authority to bind it.
          </p>
        </div>

        <Section id="services" title="1. What we do">
          <p>
            We design and build websites and provide ongoing hosting, updates,
            and support. Our published plans are:
          </p>
          <Bullets
            items={[
              "Business — a conversion-focused business website, starting at the price listed on our pricing page, plus a monthly hosting, updates, and support fee.",
              "Ecommerce — a store build, starting at the price listed on our pricing page, plus the same monthly fee.",
              "3D / Animated — a scoped custom build, starting at the price listed on our pricing page, plus the same monthly fee.",
            ]}
          />
          <p>
            Published prices are starting points. The final scope and price for
            your project are set in a written quote or proposal we send you
            after our call. Where that quote conflicts with these Terms, the
            quote controls for the specific point it addresses, and these Terms
            control for everything else.
          </p>
        </Section>

        <Section id="mockup" title="2. The free mockup">
          <p>
            We provide a free design mockup before you pay anything. The mockup
            is a design concept, not a finished website, and it is provided for
            you to evaluate our work.
          </p>
          <Bullets
            items={[
              "There is no charge and no obligation to proceed after seeing your mockup.",
              "Until you have paid for a project, Mind Media retains all ownership of the mockup and the designs in it. You may not use, copy, publish, or hand the mockup to another developer to build from.",
              "Mockup turnaround times we quote are good-faith estimates, not commitments.",
              "We may decline to produce a mockup for any reason, including if we don't think we're the right fit.",
            ]}
          />
        </Section>

        <Section id="fees" title="3. Prices, payment, and the monthly plan">
          <p className="font-medium text-foreground">Project fees</p>
          <p>
            Project fees are quoted in US dollars and are due on the schedule in
            your quote. Work starts once the first payment clears. If an invoice
            is more than 15 days past due, we may pause work and pause hosting
            after written notice.
          </p>

          <p className="pt-2 font-medium text-foreground">
            Monthly hosting, updates, and support
          </p>
          <p>
            Our monthly plan covers hosting, security and platform updates, and
            the ongoing edits described in your quote. It does not cover new
            pages, new features, redesigns, or third-party costs such as domain
            registration, premium plugins, stock imagery, fonts, or paid app
            subscriptions. We will quote those separately before doing the work.
          </p>

          {/* REVIEW: California's Automatic Renewal Law (Bus. & Prof. Code 17600 et seq.)
              applies to your $99/mo plan. It requires the renewal terms to be presented
              clearly and conspicuously BEFORE checkout, an affirmative opt-in, an emailed
              acknowledgment, and a cancellation method at least as easy as sign-up.
              Putting it on this page alone is NOT sufficient — it also has to appear at
              the point of payment. This is the highest-risk item on the page. */}
          <Callout title="Automatic renewal — please read">
            <p>
              The monthly plan renews automatically each month at the rate in
              your quote, charged to the payment method on file, until you
              cancel. There is no minimum term and no cancellation fee.
            </p>
            <p>
              <span className="font-semibold text-foreground">
                To cancel, email{" "}
                <a
                  className="underline underline-offset-4"
                  href={`mailto:${CONTACT_EMAIL}?subject=Cancel%20my%20plan`}
                >
                  {CONTACT_EMAIL}
                </a>{" "}
                with the subject line &ldquo;Cancel my plan.&rdquo;
              </span>{" "}
              We will confirm in writing within 2 business days. Cancellation
              takes effect at the end of your current billing month. We do not
              prorate partial months, and we will not charge you again after
              that.
            </p>
            <p>
              We will give you at least 30 days&rsquo; written notice before any
              price increase, and you may cancel before it takes effect.
            </p>
          </Callout>

          <p>
            Payments are handled by our third-party payment processor. Fees do
            not include sales or use tax, which we will add where required.
          </p>
        </Section>

        <Section id="guarantee" title="4. The money-back guarantee">
          <p>
            Our site says: not happy after round one? Full refund, no questions
            asked. We mean it, and here is exactly how it works.
          </p>
          <Bullets
            items={[
              "After we deliver the first round of design for your project, you have 7 days to tell us in writing that you're not happy and want a refund.",
              "We refund 100% of what you have paid toward the project fee. We do not ask you to justify the decision.",
              "Refunds are issued to the original payment method within 10 business days.",
              "If you take the refund, the engagement ends, no site is launched, and ownership of all designs and files stays with Mind Media. You may not use the refunded work.",
              "Monthly plan fees already incurred for hosting a live site are not part of the guarantee, because that service was already delivered.",
            ]}
          />
          {/* REVIEW: The 7-day window and the "round one" trigger are the two numbers
              that decide how much exposure this creates. Your homepage says "not happy
              after round one," which is what this mirrors. If you ever change the
              homepage to say something broader, this section has to change with it —
              a Terms page that contradicts your own marketing is evidence against you,
              not protection. */}
          <p>
            Once you approve the design and we move into build, the guarantee
            period has passed and project fees are non-refundable. You can still
            cancel the monthly plan at any time under Section 3.
          </p>
        </Section>

        <Section id="ownership" title="5. You own your website">
          <p>
            Once you have paid your project fee in full, you own the final
            website we deliver: the design, the page layouts, the custom code we
            wrote for you, and the content. We assign that ownership to you
            outright. Two sensible limits apply:
          </p>
          <Bullets
            items={[
              <>
                <span className="font-medium text-foreground">
                  Third-party materials.
                </span>{" "}
                Fonts, stock photography, icons, plugins, themes, and libraries
                are licensed, not owned. You receive whatever license the
                provider grants, and you are responsible for keeping those
                licenses current after launch. We will tell you what they are at
                handoff.
              </>,
              <>
                <span className="font-medium text-foreground">
                  Our general know-how.
                </span>{" "}
                We keep ownership of the generic components, snippets, and
                methods we use across all of our projects. Your ownership of the
                finished site is not affected by this, and we will never claim a
                right that stops you from using, editing, or moving your site.
              </>,
            ]}
          />
          <p>
            <span className="font-medium text-foreground">
              If you leave, your hosting comes with you.
            </span>{" "}
            When you cancel, we will provide a complete export of your site
            files and database, and cooperate with your new host&rsquo;s
            migration, at no charge, for up to 30 days after cancellation. After
            30 days we may bill our standard hourly rate for further migration
            help, and we may delete our copies of your files after 60 days, so
            keep your own backup.
          </p>
          <p>
            You keep ownership of everything you give us — your logo, copy,
            photos, and brand assets. You grant us a license to use them for the
            purpose of building and supporting your site.
          </p>
          <p>
            <span className="font-medium text-foreground">Portfolio.</span> We
            may display your finished site, screenshots of it, and your business
            name and logo in our portfolio, case studies, and marketing, unless
            you ask us in writing not to. We will honor that request.
          </p>
        </Section>

        <Section id="your-responsibilities" title="6. What we need from you">
          <p>
            Projects stall for one reason more than any other: waiting on
            content. To keep your timeline, you agree to:
          </p>
          <Bullets
            items={[
              "Provide copy, images, logins, and brand assets promptly when we ask for them.",
              "Give consolidated feedback within 5 business days of each round.",
              "Confirm that you own or have permission to use everything you send us, and that it doesn't infringe anyone's rights or break any law.",
              "Keep your own domain registration, and give us the access we need to point it.",
            ]}
          />
          <p>
            <span className="font-medium text-foreground">
              About our turnaround promises.
            </span>{" "}
            Where we advertise a delivery window — for example, a live site
            within 72 hours of your approval — that clock runs only while we
            have everything we need from you. If we are waiting on content,
            access, feedback, or payment, the clock pauses until we have it.
          </p>
          <p>
            If a project goes 30 days with no response from you, we may treat it
            as inactive, and restarting it may require a new quote.
          </p>
        </Section>

        <Section id="results" title="7. What we don't promise">
          <p>
            We build sites designed to convert, and we are proud of the results
            our clients get. But results depend on your market, your pricing,
            your offer, your ad spend, your reviews, your responsiveness to
            leads, and factors none of us control.
          </p>
          <Callout title="No guarantee of results">
            <p>
              Any figures, charts, testimonials, or case studies on our site
              reflect the experience of specific clients. They are not a
              promise, guarantee, or prediction of what your business will
              earn. Individual results vary, and many businesses see different
              outcomes.
            </p>
            <p>
              We do not guarantee any specific search ranking, traffic volume,
              lead count, conversion rate, or revenue. We build sites that are
              technically sound and search-friendly. Search engines,
              advertising platforms, and social networks change their systems
              at their own discretion, and we are not responsible for the
              effect of those changes.
            </p>
          </Callout>
          {/* REVIEW: Your homepage headline is "Websites That Actually Make Money" and
              you display lead-increase and sales-increase charts. Under the FTC's
              endorsement and testimonial rules, performance claims like those need to
              reflect what customers generally achieve, or be accompanied by a clear
              disclosure of typical results. This section is your backstop, but a
              disclaimer on /terms does not cure a misleading claim on the homepage.
              Consider adding a short "individual results vary" line near those charts. */}
        </Section>

        <Section id="acceptable-use" title="8. Acceptable use">
          <p>
            You agree not to use our site or services to publish or distribute
            anything unlawful, infringing, defamatory, deceptive, or malicious;
            to send spam; to attempt to gain unauthorized access to our systems
            or those of our providers; or to interfere with the operation of the
            site. We may suspend or terminate service for a serious or repeated
            breach of this section, with notice where practical.
          </p>
        </Section>

        <Section id="third-party" title="9. Third-party services">
          <p>
            Your site may rely on third-party platforms and services — hosting,
            Shopify or a similar commerce platform, payment processors, email
            providers, plugins, analytics, and booking tools. Those services are
            governed by their own terms, and their availability, pricing, and
            features are outside our control. We are not liable for their
            outages, changes, price increases, or discontinuation, but we will
            work with you in good faith to find a replacement if one stops
            serving you.
          </p>
        </Section>

        <Section id="warranty" title="10. Warranty and disclaimer">
          <p>
            We warrant that we will perform our services in a professional and
            workmanlike manner. For 30 days after launch, we will fix any defect
            in the code we wrote — something not working as designed — at no
            charge. This warranty does not cover changes you or a third party
            make to the site, third-party software failures, or new feature
            requests.
          </p>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">
            Except as stated in this section, the site and services are provided
            &ldquo;as is&rdquo; and &ldquo;as available,&rdquo; and Mind Media
            disclaims all other warranties, express or implied, including
            implied warranties of merchantability, fitness for a particular
            purpose, title, and non-infringement, and any warranty of
            uninterrupted or error-free operation.
          </p>
        </Section>

        <Section id="liability" title="11. Limitation of liability">
          <p className="text-sm uppercase tracking-wide text-muted-foreground">
            To the maximum extent permitted by law, Mind Media will not be
            liable for indirect, incidental, special, consequential, exemplary,
            or punitive damages, or for lost profits, lost revenue, lost data,
            or business interruption, even if we were advised such damages were
            possible.
          </p>
          <p className="text-sm uppercase tracking-wide text-muted-foreground">
            Our total liability arising out of or relating to these Terms or
            our services will not exceed the total amount you paid us in the 12
            months before the event giving rise to the claim.
          </p>
          <p>
            Nothing in this section limits our obligation to honor the
            money-back guarantee in Section 4, or limits liability that cannot
            be limited by law, including liability for fraud, willful
            misconduct, or gross negligence. Some jurisdictions do not allow
            some of these limits, in which case they apply to the fullest
            extent permitted.
          </p>
        </Section>

        <Section id="indemnity" title="12. Indemnification">
          <p>
            You agree to indemnify and hold Mind Media harmless from claims,
            damages, and reasonable legal costs arising from content or
            materials you provided to us, from your use of the site after
            launch, or from your breach of these Terms or of any law. We will
            notify you promptly of any such claim and let you control the
            defense, provided you do not settle in a way that imposes an
            obligation on us without our consent.
          </p>
        </Section>

        <Section id="termination" title="13. Termination">
          <p>
            You may cancel the monthly plan at any time under Section 3. Either
            party may terminate an active project for material breach if the
            breach is not cured within 15 days of written notice. If you
            terminate a project after the guarantee period in Section 4, you owe
            us for work completed through the termination date.
          </p>
          <p>
            On termination, Sections 5 (ownership), 7 (what we don&rsquo;t
            promise), 10 through 12, and 14 through 16 survive.
          </p>
        </Section>

        <Section id="disputes" title="14. Disputes">
          <p>
            <span className="font-medium text-foreground">Talk to us first.</span>{" "}
            Before filing anything, email{" "}
            <a
              className="underline underline-offset-4"
              href={`mailto:${CONTACT_EMAIL}`}
            >
              {CONTACT_EMAIL}
            </a>{" "}
            describing the problem and what you want. We will respond within 10
            business days and both sides agree to try in good faith to resolve
            it for 30 days. Most problems end here.
          </p>
          <p>
            <span className="font-medium text-foreground">
              Governing law and venue.
            </span>{" "}
            These Terms are governed by the laws of the State of California,
            without regard to its conflict-of-laws rules. Any dispute not
            resolved informally will be brought in the state or federal courts
            located in San Diego County, California, and both parties consent to
            that jurisdiction. Either party may bring a qualifying claim in
            small claims court instead.
          </p>
          {/* REVIEW: California law + San Diego County venue is used here because that
              is where you operate and where a court would most likely find jurisdiction
              over you anyway. Your Delaware formation governs the LLC's internal affairs,
              not your customer contracts, so the two are not in conflict. If your attorney
              prefers Delaware law and venue, change both sentences together — and know
              that a client's home-state consumer statutes can still override a choice-of-law
              clause regardless of what this says.

              Also note: there is deliberately NO mandatory arbitration clause here.
              Arbitration filing fees often exceed the value of a small web-project
              dispute, and the cost usually lands on the business. If you later take on
              enterprise clients, revisit this. */}
          <p>
            The UN Convention on Contracts for the International Sale of Goods
            does not apply.
          </p>
        </Section>

        <Section id="changes" title="15. Changes to these Terms">
          <p>
            We may update these Terms. We will change the &ldquo;Last
            updated&rdquo; date above, and for material changes we will notify
            active clients by email at least 30 days before they take effect.
            Changes do not apply retroactively to a project already underway
            unless you agree in writing. Continuing to use the site or services
            after a change takes effect means you accept the updated Terms.
          </p>
        </Section>

        <Section id="general" title="16. General">
          <Bullets
            items={[
              "Entire agreement. These Terms, plus your signed quote or proposal and our Privacy Policy, are the whole agreement between us and replace any earlier discussions.",
              "Severability. If a provision is found unenforceable, the rest stays in force.",
              "No waiver. If we don't enforce something right away, we haven't given up the right to enforce it later.",
              "Assignment. You may not assign these Terms without our written consent, except to a successor of your business. We may assign them in connection with a merger or sale.",
              "Independent contractor. We are an independent contractor, not your employee, partner, or joint venturer.",
              "Force majeure. Neither party is liable for delays caused by events outside its reasonable control, including outages of upstream providers, natural disasters, and government action.",
              "Notices. Written notice means email to the addresses on file, or mail to the address below.",
            ]}
          />
        </Section>

        <Section id="contact" title="17. Contact">
          <p className="rounded-lg border border-border bg-muted/40 p-5">
            <span className="font-medium text-foreground">Mind Media LLC</span>
            <br />
            <a
              className="underline underline-offset-4"
              href={`mailto:${CONTACT_EMAIL}`}
            >
              {CONTACT_EMAIL}
            </a>
            <br />
            {MAILING_ADDRESS}
          </p>
        </Section>

    </LegalPageShell>

  );
}

export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms of Service — Mind Media" },
      { name: "description", content: "The terms that govern Mind Media web design, development, and marketing services, including payments, revisions, and guarantees." },
      { property: "og:title", content: "Terms of Service — Mind Media" },
      { property: "og:description", content: "The terms that govern Mind Media web design, development, and marketing services, including payments, revisions, and guarantees." },
      { property: "og:url", content: "https://mindmedia.co/terms" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://mindmedia.co/terms" }],
  }),
  component: TermsPage,
});
